<script lang="ts">
/**
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.4.0
 * @copyright  Copyright (C) 2024-2026, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 * @changelog
 *  2024-08-23 (1.0.0) (jhoward)
 *    - Refactor textual default section from <textual-menu> to <default-textual-section>.
 *
 *  2024-08-26 (1.1.0) (jhoward)
 *    - Handle selected menu items via store in <default-textual-section>.
 *    - Add scoped CSS in <default-textual-section>.
 *    - Customize item grid in <default-textual-section> based on rows in each item.
 *
 *  2024-12-03 (1.1.1) (jhoward)
 *    - Add isCartEnabled prop to <default-textual-section>.
 *    - Do not show "favorite" icon when isCartEnabled prop is set to false in <default-textual-section>.
 *
 *  2025-02-11 (1.2.0) (jhoward)
 *    - Add <menu-schedules> to the <defaultTextualSection> component if in admin view.
 *
 *  2025-04-08 (1.3.0) (jhoward)
 *    - Add "menuItemComponent" prop to <defaultTextualSection>.
 *    - Add "menuItemTagLocation" prop to <defaultTextualSection>.
 *    - Add "contentAreaTopLayout" prop to <defaultTextualSection>.
 *    - Add "contentAreaBottomLayout" prop to <defaultTextualSection>.
 *    - Add "isTagLegendHidden" prop to <defaultTextualSection>.
 *    - Add "isTitleBoxVisible" prop to <defaultTextualSection>.
 *    - Add "isTitleBufferVisible" prop to <defaultTextualSection>.
 *    - Add "sectionModifier" prop to <defaultTextualSection>.
 *    - Add "sectionMenuType" prop to <defaultTextualSection>.
 *    - Auto-detect long price strings and adjust UI accordingly in <defaultTextualSection>.
 *    - Remove "textual_" prefix from class names in <defaultTextualSection>.
 *    - Add "sectionTitleImage" slot to facilitate showing an image above the title in <defaultTextualSection>.
 *    - Add "beforeTopContentArea" slot to facilitate showing content above the top content area in <defaultTextualSection>.
 *    - Add "beforeGroupTitle" slot to facilitate showing a tag before the title in <defaultTextualSection>.
 *    - Add section title DOM elements to match <defaultVisualSection> in <defaultTextualSection>.
 *    - Use <section-item-subgroup> to display menu items in <defaultTextualSection>.
 *    - Remove handing of "maxItemRows" in <defaultTextualSection>.
 *
 * 2026-02-17 (1.3.1) (jhoward)
 *   - Add beforeBottomContentArea slot to App\Library\Js\Components\Menu\Section\Textual\defaultTextualSection.vue for custom content injection.
 *
 * 2026-07-20 (1.4.0) (jhoward)
 *   - Add beforeBottomContentArea slot to App\Library\Js\Components\Menu\Section\Textual\defaultTextualSection.vue.
 */

import {defineComponent, PropType} from 'vue'
import MenuTagLegend from "../../menu-tag-legend.vue";
import MenuTag from "../../menu-tag.vue";
import {
  ALLOWED_ITEM_COMPONENTS,
  Menu, MENU_ITEM_TAG_LOCATIONS,
  MenuSection, SECTION_CONTENT_AREA_BOTTOM_LAYOUT, SECTION_CONTENT_AREA_TOP_LAYOUT,
  WINE_LIST_ITEM_COMPONENTS
} from "../../defs";
import {
  computedIsScheduled,
  computeItemGroupPricingTypes,
  isNormalizedScheduleActive, methodGetPreviousGroupAndSubgroupTitles, methodIsSubgroupPricingShown
} from "../../../../helpers/menu-sections";
import MenuVersion from "../../menu-version.vue";
import MenuSchedules from "../../schedule/MenuSchedules.vue";
import SectionItemSubgroup from "../SectionItemSubgroup.vue";

export default defineComponent({
  name: "defaultTextualSection",
  components: {
    SectionItemSubgroup, MenuSchedules,
    MenuVersion, MenuTag, MenuTagLegend,
  },
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu | null>,
      required: true
    },
    menuItemComponent: {
      type: String,
      default: '',
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
    menuItemTagLocation: {
      type: String as PropType<MENU_ITEM_TAG_LOCATIONS>,
      default: ''
    },
    isCartEnabled: {
      type: Boolean,
      default: false
    },
    contentAreaTopLayout: {
      type: String as PropType<SECTION_CONTENT_AREA_TOP_LAYOUT>,
      default: ''
    },
    contentAreaBottomLayout: {
      type: String as PropType<SECTION_CONTENT_AREA_BOTTOM_LAYOUT>,
      default: ''
    },
    isTagLegendHidden: {
      type: Boolean,
      default: false
    },
    isTitleBoxVisible: {
      type: Boolean,
      default: false
    },
    isTitleBufferVisible: {
      type: Boolean,
      default: false
    },
    sectionModifier: {
      type: String,
      default: ''
    },
    sectionMenuType: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      longPriceThreshold: 20,

      WINE_LIST_ITEM_COMPONENTS,
      ALLOWED_ITEM_COMPONENTS,
    };
  },
  computed: {
    isScheduled: computedIsScheduled,
    itemGroupPricingTypes: computeItemGroupPricingTypes,
    isEmpty(): boolean {
      for (let groupTitle in this.section.grouped_items) {
        for (let subgroupTitle in this.section.grouped_items[groupTitle]) {
          for (let i = 0, l = this.section.grouped_items[groupTitle][subgroupTitle].length || 0; i < l; i++) {
            if (0 === this.section.grouped_items[groupTitle][subgroupTitle][i].normalizedSchedules.length) {
              return false;
            }
            for (let ii = 0, ll = this.section.grouped_items[groupTitle][subgroupTitle][i].normalizedSchedules.length || 0; ii < ll; ii++) {
              if (isNormalizedScheduleActive(this.section.grouped_items[groupTitle][subgroupTitle][i].normalizedSchedules[ii], this.$store.state.currentDateTimeUtc, this.$store.state.currentDayOfWeek, this.$store.state.currentTimeOfDay)) {
                return false;
              }
            }
          }
        }
      }

      return true;
    },
    containerClasses(): { [key: string]: boolean } {
      return {
        'menu--section-show_bin': !!this.section.ms_show_bin,
        'menu--section-show_pricing_header': !!this.section.ms_show_pricing_header,
        'menu--section-group_by_price': !!this.section.ms_group_by_price,
        'menu--section-highlight': !!this.section.ms_highlight,
        'menu--section-desserts': this.section.ms_title.toLowerCase() === 'desserts',
        'menu--section-long_price': this.isLongPriceLayout,
      };
    },
    isPageLinkGrid(): boolean {
      return false;//!this.section.ms_group_by_price
        // && !this.section.ms_show_pricing_header
        // && !this.section.ms_show_bin;
    },
    isLongPriceLayout(): boolean {
      for (let groupTitle in this.section.grouped_items) {
        for (let subgroupTitle in this.section.grouped_items[groupTitle]) {
          for (let i = 0, l = this.section.grouped_items[groupTitle][subgroupTitle].length || 0; i < l; i++) {
            if (this.section.grouped_items[groupTitle][subgroupTitle][i].price_display.length >= this.longPriceThreshold) {
              return true;
            }
          }
        }
      }

      return false;
    },
  },
  methods: {
    isSubgroupPricingShown: methodIsSubgroupPricingShown,
    getPreviousGroupAndSubgroupTitles: methodGetPreviousGroupAndSubgroupTitles,
  },
})
</script>

<template>
  <div v-show="isScheduled && !isEmpty"
       :data-section-id="section.ms_id"
       class="body-container no-images show-all-items textual_menu--section menu--section" :class="containerClasses">
    <div class="page-link-group-hash" :id="section.ms_id + '-MS'"></div>

    <slot name="sectionTitleImage"></slot>

    <div v-if="'above-title' === contentAreaTopLayout && section.ms_content_area_top.length" class="menu--page_content menu--page_content-top page-link-group-copy">
      <div class="viewport" v-html="section.ms_content_area_top"></div>
    </div>

    <h2 class="menu--section_title" tabindex="-1">
      <span v-if="isTitleBufferVisible"></span>
      <span>{{ section.ms_title }}</span>
      <span v-if="isTitleBufferVisible"></span>
      <span v-if="isTitleBoxVisible" class="menu--section_title_box"></span>
    </h2>
    <h3 v-if="sectionMenuType.length" v-html="sectionMenuType" class="menu--section_menu_type"></h3>
    <h3 v-if="!sectionMenuType.length && section.ms_subtitle.length" v-html="section.ms_subtitle"
        class="menu--section_subtitle"></h3>
    <h4 v-if="sectionMenuType.length && section.ms_subtitle.length" v-html="section.ms_subtitle"
        class="menu--section_subtitle"></h4>

    <menu-schedules v-if="$store.state.isAdminView && section.normalizedSchedules.length"
                    :normalized-schedules="section.normalizedSchedules"></menu-schedules>

    <slot name="beforeTopContentArea"></slot>

    <div v-if="'' === contentAreaTopLayout && section.ms_content_area_top.length" class="menu--page_content menu--page_content-top page-link-group-copy">
      <div class="viewport" v-html="section.ms_content_area_top"></div>
    </div>

    <div v-for="(group, groupTitle) in section.grouped_items" class="viewport">
      <h3 v-if="groupTitle.length && !group.hasOwnProperty(groupTitle)" class="menu--section_group_title">
        <slot name="beforeGroupTitle"></slot>
        <span>{{ groupTitle }}</span>
      </h3>
      <template v-for="(subgroup, subgroupTitle) in group">
        <section-item-subgroup :menu-item-component="(menuItemComponent.length) ? menuItemComponent : 'default-item'"
                               :menu-item-tag-location="menuItemTagLocation"
                               :section="section"
                               :pricing-types="itemGroupPricingTypes[groupTitle][subgroupTitle]"
                               :items="subgroup"
                               :is-subgroup-pricing-shown="isSubgroupPricingShown(groupTitle, subgroupTitle)"
                               :group-title="groupTitle" :subgroup-title="subgroupTitle"
                               :is-page-link-grid="isPageLinkGrid"></section-item-subgroup>
      </template>
    </div>

    <slot name="beforeBottomContentArea"></slot>

    <menu-tag-legend v-if="!isTagLegendHidden && section.section_tags.length" :tags="section.section_tags"
                     menu-type="textual"></menu-tag-legend>

    <div class="menu--page_content menu--page_content-bottom page-link-group-copy" v-if="'none' !== contentAreaBottomLayout && section.ms_content_area_bottom.length">
      <div v-html="section.ms_content_area_bottom"
           class="viewport"></div>
    </div>

    <menu-version v-if="section.ms_menu_version_override"
                  :version="section.ms_menu_version_override || ''"></menu-version>
  </div>
</template>

<style scoped>
/*.textual_menu--section.menu--section-show_bin,
.textual_menu--section.menu--section-show_pricing_header {
  font-size: 0.9em;
}*/

.textual_menu--section.menu--section-show_bin .menu--section_group_title,
.textual_menu--section.menu--section-show_pricing_header .menu--section_group_title {
  text-align: center;
  font-weight: bold;
  padding: 0.2em 0;
}

.menu--section-show_bin .menu--section_group_title:not(:first-child),
.menu--section-show_pricing_header .menu--section_group_title:not(:first-child) {
  margin-top: 1em;
}

h2 {
  font-size: 1.3rem !important;
}

.body-container.textual_menu--section > .viewport::before {
  display: none;
}

.body-container.textual_menu--section > .viewport {
  padding-top: 24px;
}

.textual_menu--section .menu--section_title ~ .viewport {
  margin-top: 1.5em;
}

.textual_menu--section.menu--section-show_pricing_header:deep(.menu--section_subgroup_title span:first-child),
.textual_menu--section.menu--section-group_by_price:deep(.menu--section_subgroup_title span:first-child),
.textual_menu--section.menu--section-show_pricing_header.menu--section-show_bin:deep(.menu--item_subgroup-show_BIN h3 span:nth-child(2)),
.textual_menu--section.menu--section-group_by_price.menu--section-show_bin:deep(.menu--item_subgroup-show_BIN h3 span:nth-child(2)) {
  text-align: initial;
}

.textual_menu--section.menu--section-show_pricing_header:deep(.menu--item h3 span:not(:first-child)),
.textual_menu--section.menu--section-group_by_price:deep(.menu--item h3 span:not(:first-child)) {
  text-align: center;
}

.textual_menu--section.menu--section-show_pricing_header:deep(.menu--item_subgroup .menu--item h3),
.textual_menu--section.menu--section-group_by_price:deep(.menu--item_subgroup .menu--item h3) {
  background: initial;
  /*font-size: 0.8rem;*/
  margin: 0;
  padding: 0;
}

.textual_menu--section.menu--section-group_by_price:deep(.menu--item_subgroup .menu--item h3) {
  grid-template-columns: 1fr;
}

.textual_menu--section.menu--section-show_pricing_header:deep(.menu--item_subgroup),
.textual_menu--section.menu--section-group_by_price:deep(.menu--item_subgroup) {
  grid-gap: 0.5em;
}

.textual_menu--section.body-container:deep(.viewport) {
  padding-top: 0;
  padding-right: 2rem;
  padding-left: 2rem;
  width: 100%;
}

.textual_menu--section:deep(.menu--item_subgroup .menu--section_subgroup_title) {
  margin-top: 0.5em;
  margin-bottom: 0;
}

/*.textual_menu--section.menu--section-show_pricing_header:deep(.menu--item_price),
.textual_menu--section.menu--section-show_bin:deep(.menu--item_price) {
  text-align: center;
}*/

/*.textual_menu--section:deep(.menu--item_price) {
  text-align: initial;
}*/
</style>